/**
 * v4 § aiRespond 정확한 매핑.
 *
 * W2: 실제 BE LLM 호출로 교체. mock 응답은 입력 키워드 기반.
 */

export interface AICardData {
  header: string;
  title: string;
  desc: string; // <strong> / <br> 허용
  actions?: Array<{
    label: string;
    primary?: boolean;
    // 클라이언트 네비게이션용 액션 키(goToCare, showRoutineAnalysis, save-meal, recommend-next 등)
    action?: string;
    topic?: string;
  }>;
}

export interface AIResponse {
  messages: string[]; // 각 메시지는 <strong> / <br> 허용 (HTML)
  card?: AICardData;
}

/** 회의록 09:18 - 자연어 키워드 매칭으로 mock 응답 (W2에 LLM 교체) */
export function aiRespond(userText: string): AIResponse {
  const t = userText.toLowerCase();

  // 1. 식사 추천
  if (
    t.includes('점심') ||
    t.includes('저녁') ||
    t.includes('아침') ||
    t.includes('뭐 먹') ||
    t.includes('어떻게 먹') ||
    t.includes('메뉴') ||
    t.includes('추천') ||
    t.includes('식사')
  ) {
    return {
      messages: [
        'GLP-1 효과 유지하려면 <strong>단백질 위주</strong>로 가시는 게 좋아요.<br>오늘 단백질 섭취량이 30g 부족해요.',
      ],
      card: {
        header: '🍱 추천',
        title: '오늘 점심 추천 메뉴',
        desc: '닭가슴살 샐러드 또는 두부 비빔밥처럼 단백질이 충분한 메뉴를 권해드려요. 매운 음식은 부작용 있으셨던 분들은 피하세요.',
        actions: [
          { label: '저장하기', primary: true },
          { label: '다른 추천', primary: false },
        ],
      },
    };
  }

  // 2. 용량 증량
  if (t.includes('용량') || t.includes('늘려') || t.includes('증량') || t.includes('단계')) {
    return {
      messages: [
        '용량 증량은 <strong>몸의 적응 신호</strong>를 보고 결정해요. 보통 4주 단위로 단계적으로 올라가요.',
      ],
      card: {
        header: '💉 용량 증량 가이드',
        title: '증량 전 체크 항목',
        desc: '1) 현재 용량 <strong>4주 이상</strong> 유지했나요?<br>2) 부작용(메스꺼움·구토)이 가벼운 수준인가요?<br>3) 마지막 1~2주 식사·수분 섭취 정상인가요?<br><br>모두 OK면 다음 진료 때 의사와 상의하세요. 임의로 증량하시면 안 돼요.',
        actions: [
          { label: '내 부작용 기록 보기', primary: true },
          { label: '진료 보고서 만들기', primary: false },
        ],
      },
    };
  }

  // 3. 부작용
  if (
    t.includes('부작용') ||
    t.includes('메스꺼') ||
    t.includes('속이') ||
    t.includes('울렁') ||
    t.includes('토') ||
    t.includes('변비') ||
    t.includes('설사') ||
    t.includes('두통')
  ) {
    return {
      messages: [
        'GLP-1 부작용은 대부분 <strong>초기 4~8주</strong>에 집중되고 점차 줄어들어요. 지금 어떤 증상이 있으세요?',
      ],
      card: {
        header: '⚠️ 부작용 가이드',
        title: '흔한 부작용별 대처법',
        desc: '<strong>메스꺼움·구토</strong>: 식사량 절반·미지근한 물 섭취<br><strong>변비</strong>: 수분 2L+ · 식이섬유 늘리기<br><strong>설사</strong>: 자극적 음식 피하기<br><strong>두통</strong>: 수분 부족 가능성 — 물 먼저<br>증상이 24시간 이상 지속되면 의사 상담 권장드려요.',
        actions: [
          { label: '증상 기록하기', primary: true },
          { label: '진료 보고서 추가', primary: false },
        ],
      },
    };
  }

  // 4. 근손실
  if (t.includes('근손실') || t.includes('근육')) {
    return {
      messages: [
        'GLP-1 환자분들의 가장 흔한 걱정이에요. <strong>단백질 + 근력 운동</strong>이 핵심이에요.',
      ],
      card: {
        header: '💪 근손실 방지',
        title: '하루 단백질 + 운동 가이드',
        desc: '체중 1kg당 단백질 1.2~1.6g 권장 (길동님 약 82~110g)<br>주 2~3회 근력 운동 (다리·등·가슴)<br>체중계 대신 체성분계로 근육량 추적',
        actions: [
          { label: '케어 가이드 보기', primary: true, action: 'goToCare', topic: 'muscle' },
          { label: '단백질 식단', primary: false, action: 'goToCare', topic: 'protein' },
        ],
      },
    };
  }

  // 5. 주사/약
  if (t.includes('주사') || t.includes('약') || t.includes('맞')) {
    return {
      messages: [
        '오늘 위고비 0.5mg 맞을 차례예요. 지난주 0.25mg 후 메스꺼움 2회 있으셨죠.<br>오늘은 <strong>식사 30분 전 물 충분히</strong> 드시고 가벼운 식사로 시작하세요.',
      ],
    };
  }

  // 6. 정체기
  if (
    t.includes('안 빠') ||
    t.includes('정체') ||
    t.includes('안빠') ||
    (t.includes('체중') && (t.includes('그대로') || t.includes('안 줄')))
  ) {
    return {
      messages: [
        '<strong>정체기는 자연스러운 현상</strong>이에요. GLP-1 사용자 70% 이상이 4~8주차에 한 번 정체기를 겪어요.<br>몸이 새 체중에 적응하는 시기라 며칠 안 빠진다고 효과 없는 게 아니에요.',
      ],
      card: {
        header: '📊 정체기 체크리스트',
        title: '먼저 4가지를 점검해보세요',
        desc: '1) <strong>체중 측정 시점</strong> 매일 같은 시간(기상 직후)?<br>2) <strong>단백질</strong> 체중 1kg당 1.2~1.6g 섭취?<br>3) <strong>수분</strong> 하루 2L 이상?<br>4) <strong>근력 운동</strong> 주 2~3회?<br>모두 OK인데 2주 이상 정체면 의사와 상담 권장드려요.',
        actions: [
          { label: '내 데이터 점검', primary: true },
          { label: '운동 가이드', primary: false },
        ],
      },
    };
  }

  // 7. 체중 일반
  if (t.includes('체중') || t.includes('몸무게') || t.includes('살')) {
    return {
      messages: [
        '이번 주 -1.4kg 빠지셨어요. 좋은 흐름이에요 🌿<br>다만 단백질 섭취가 적어서 근손실이 우려돼요.',
      ],
    };
  }

  // 8. 요요
  if (t.includes('요요') || t.includes('중단') || t.includes('끊')) {
    return {
      messages: [
        '약 중단 후 12주가 가장 중요해요. 지금부터 식습관·운동 습관을 단단히 만들어 두면 요요 위험이 크게 줄어요.',
      ],
      card: {
        header: '🌱 요요 예방',
        title: '약 중단 후 1년이 관건이에요',
        desc: 'GLP-1 중단 후 평균 <strong>2/3 이상 체중 회복</strong>이 일반적이에요.<br>1) <strong>근육량 유지</strong>가 핵심 - 기초대사량 유지<br>2) 식습관 천천히 바꾸기<br>3) 목표 도달 후 6개월 유지기',
        actions: [
          { label: '케어 가이드 보기', primary: true, action: 'goToCare', topic: 'yoyo' },
          { label: '운동 시작', primary: false, action: 'goToCare', topic: 'exercise' },
        ],
      },
    };
  }

  // 9. 인사
  if (t.includes('안녕') || t.includes('하이') || t.includes('헬로')) {
    return { messages: ['안녕하세요 길동님 🌿<br>오늘 컨디션은 어떠세요?'] };
  }

  // 짧거나 모호한 입력
  if (t.length <= 3 || /^[ㅋㅎㅠㅜㅡ\s.!?]+$/.test(t)) {
    const shortReplies = [
      '편하게 더 말씀해주세요 🌿',
      '뭘 도와드릴까요? 식사·체중·부작용·운동 뭐든 좋아요.',
      '좀 더 구체적으로 알려주시면 더 정확히 도와드릴 수 있어요.',
    ];
    return {
      messages: [shortReplies[Math.floor(Math.random() * shortReplies.length)]],
    };
  }

  // Fallback
  const fallbackReplies = [
    '음식·부작용·체중·약·운동 중에 어떤 게 궁금하세요? 위 추천 칩에서 골라보셔도 좋아요.',
    '제가 잘 이해 못했어요. 다른 표현으로 다시 말씀해주실래요? 🌿',
    '좀 더 구체적으로 알려주시면 도움이 될 것 같아요. 예를 들어 "오늘 메스꺼워요" 또는 "단백질 어떻게 채울까요?" 처럼요.',
    '아래 추천 질문 중에 비슷한 게 있을까요? 없으면 좀 더 자세히 적어주세요.',
  ];
  return {
    messages: [fallbackReplies[Math.floor(Math.random() * fallbackReplies.length)]],
  };
}
